/**
 * Refresh Stage 9: Editorial Assembly
 *
 * LLM stage. Assembles all brief sections (kept + drafted) into cohesive
 * markdown, smooths transitions, maintains voice consistency. Same pattern
 * as creation Stage4cFinalizeV2.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function InputSummary() {
  const { document } = useArticleContext();
  const brief = (document as any)?.refresh?.brief;
  const sections = brief?.sections || [];
  const meta = brief?.meta || {};

  const keptCount = sections.filter((s: any) => s.action === 'keep').length;
  const draftedCount = sections.filter((s: any) => s.action !== 'keep' && s.drafted_body).length;
  const pendingCount = sections.filter((s: any) => s.action !== 'keep' && !s.drafted_body).length;
  const readyCount = keptCount + draftedCount;

  return (
    <div className="space-y-2 text-xs text-gray-600 bg-gray-50 rounded border p-2">
      <div>
        Sections ready: {readyCount} / {sections.length}
        {pendingCount > 0 && (
          <span className="text-amber-600 ml-2">
            ({pendingCount} still need drafting)
          </span>
        )}
      </div>
      <div className="text-gray-500">
        {keptCount} kept, {draftedCount} drafted
      </div>
      {meta.target_word_count > 0 && (
        <div className="text-gray-500">Target word count: {meta.target_word_count}</div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const markdown = doc.article?.markdown;
  const diffSummary = (doc as any)?.refresh?.diff_summary;
  // article.markdown is pre-populated from WordPress. Only show it here
  // if the assembly stage has actually run (indicated by diff_summary existing).
  if (!diffSummary) {
    return <div className="text-sm text-gray-500">Not assembled yet. Run this stage to combine all drafted sections.</div>;
  }
  if (!markdown) {
    return <div className="text-sm text-gray-500">No assembled article yet.</div>;
  }
  const wordCount = markdown.split(/\s+/).filter(Boolean).length;

  return (
    <div className="space-y-2">
      {diffSummary && (
        <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2">
          <div className="font-medium text-blue-800 mb-1">Diff summary</div>
          <div className="text-blue-700">{diffSummary}</div>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="text-xs font-medium text-gray-700">Assembled article</div>
        <div className="text-xs text-gray-400">{wordCount} words</div>
      </div>
      <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 border rounded p-3 max-h-[500px] overflow-y-auto">
        {markdown}
      </pre>
    </div>
  );
}

export function RefreshAssemblyV2() {
  const { document } = useArticleContext();
  const brief = (document as any)?.refresh?.brief;
  const sections = brief?.sections || [];
  const allReady = sections.length > 0 && sections.every((s: any) =>
    s.action === 'keep' || s.drafted_body
  );

  return (
    <StagePanel
      stageId="refresh_9_assembly"
      isLlmStage
      title="Refresh: Assembly"
      description="Assembles all sections into cohesive markdown, smooths transitions, and ensures voice consistency."
      runDisabled={!allReady}
      runDisabledReason={!allReady ? 'All sections must be kept or drafted first' : undefined}
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
